import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { hasPermission } from '@/app/store/auth'
import { useMe } from '@/features/auth/hooks/useAuth'
import { templatesApi, type Template } from '../api'

export interface TemplateAccess {
    /**
     * 'checking' until /auth/me and the authoring question have both answered, 'failed' if either
     * could not. Neither is a denial and neither is a grant: nothing is known yet, and the screens
     * say so rather than guess.
     */
    status: 'checking' | 'ready' | 'failed'
    /** templates.update: renaming, restructuring, hiding. False until status is 'ready'. */
    canEdit: boolean
    /** templates.delete. False until status is 'ready'. */
    canDelete: boolean
    /** This deployment is where shipped templates are drawn. False until status is 'ready'. */
    authoring: boolean
    /** May this person change this template here? Their permission, and for a shipped one, authoring. */
    canChange: (template: Pick<Template, 'IsSystem'> | null | undefined) => boolean
    /** May this person delete this template here? */
    canRemove: (template: Pick<Template, 'IsSystem'> | null | undefined) => boolean
}

/**
 * What this person may do to templates, from the permissions their roles carry and, for a
 * shipped template, from where they are.
 *
 * Permissions are read from the /auth/me query, not from the user in the auth store. The store is
 * not reliably the /auth/me shape: sign-in writes the LOGIN response into it (roles as plain
 * strings, no permissions at all), and it stays that way until AppShell's /auth/me call answers,
 * or for good if that call fails (it does not retry). It is also persisted, so after a reload it
 * holds the previous session's permissions until the refetch lands. The query's own data has
 * neither problem. It is only ever /auth/me, and until it arrives there is no data rather than a
 * stale or permission-less user. AppShell already runs the same query, so this adds no request of
 * its own. It shares the cached answer, and asks again only when that answer is stale or was a
 * failure.
 *
 * Shipped templates (IsSystem) are VaultSentinel's. They are changed only where the deployment has
 * template authoring switched on (SHIPPED_CONTENT_AUTHORING_ENABLED, backend lib/shipped-authoring.js),
 * and are read-only everywhere else, whatever a person's role. The server enforces this; the
 * screens only avoid offering what it would refuse.
 *
 * There is deliberately no fallback to "is an admin": the routes are admin-guarded, but the
 * server checks templates.update / templates.delete, and an admin's role may or may not carry them.
 */
export function useTemplateAccess(): TemplateAccess {
    const { data: me, isError: meFailed } = useMe()
    // A deployment setting: it changes only with a redeploy, so it is asked once per page load.
    const authoringQuery = useQuery({
        queryKey: ['template-authoring'],
        queryFn: async () => (await templatesApi.authoring()).data.data.enabled === true,
        staleTime: Infinity,
    })

    const failed = meFailed || authoringQuery.isError
    const ready = !!me && authoringQuery.isSuccess
    const status: TemplateAccess['status'] = ready ? 'ready' : failed ? 'failed' : 'checking'
    const canEdit = ready && hasPermission(me, 'templates', 'update')
    const canDelete = ready && hasPermission(me, 'templates', 'delete')
    const authoring = ready && authoringQuery.data === true
    // Stable while the answers are, so screens can memoise on them.
    const canChange = useCallback<TemplateAccess['canChange']>(
        (template) => canEdit && (!template?.IsSystem || authoring),
        [canEdit, authoring],
    )
    const canRemove = useCallback<TemplateAccess['canRemove']>(
        (template) => canDelete && (!template?.IsSystem || authoring),
        [canDelete, authoring],
    )
    return { status, canEdit, canDelete, authoring, canChange, canRemove }
}
